import React, { Component } from 'react';
import PropTypes from 'prop-types';
import CustomLoading from '../../../util_components/custom_loading';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { format_field_value } from '../../early_retention_checklist/format_value';

const STATUS_COLORS = { Red: '#dc2626', Yellow: '#d97706', Green: '#16a34a' };

// Related field pairs shown together in a single combined row/cell (each value
// stays on its own line so labels and values stay aligned).
const COMBINE_GROUPS = [
  ['five_star_reviews', 'one_two_star_reviews'],
  ['child_signups', 'child_members'],
  ['family_plan_invites', 'family_plan_members']
];

// Per-member early-retention history matrix: rows are checklist fields, columns
// are the checkpoints the member has reached (oldest first). Opened from the
// Health pill on the student profile. Read-only.
class HealthHistory extends Component {
  state = { loading: true, checkpoints: [], fields: [] };

  componentDidMount() {
    this.load();
  }

  load = () => {
    const { student_uuid } = this.props;
    const url = get_api_url(`/v2/early_retention_checklist/member_history/${student_uuid}`);
    get_api(url, true)
      .then((res) => {
        const d = res?.data?.data || {};
        this.setState({
          loading: false,
          checkpoints: d.checkpoints || [],
          fields: d.fields || []
        });
      })
      .catch(() => this.setState({ loading: false }));
  };

  // Short column header, e.g. "Day 8" / "Week 7".
  col_label = (cp) => (cp.week_number > 0 ? `Week ${cp.week_number}` : `Day ${cp.day_number}`);

  // Collapse the configured pairs into combined rows. Each returned row is a
  // list of field defs rendered together (one line each); most rows hold a
  // single field.
  build_rows = () => {
    const byKey = {};
    this.state.fields.forEach((f) => {
      byKey[f.key] = f;
    });
    const primaryOf = {};
    const merged = {};
    COMBINE_GROUPS.forEach((keys) => {
      if (keys.every((k) => byKey[k])) {
        primaryOf[keys[0]] = keys.map((k) => byKey[k]);
        keys.slice(1).forEach((k) => {
          merged[k] = true;
        });
      }
    });
    const rows = [];
    this.state.fields.forEach((f) => {
      if (merged[f.key]) return;
      rows.push({ key: f.key, parts: primaryOf[f.key] || [f] });
    });
    return rows;
  };

  render_cell = (field, cp) => {
    const src = (field.kind === 'auto' ? cp.auto_data : cp.reply) || {};
    const raw = src[field.key];

    if (field.shape === 'status') {
      const bg = STATUS_COLORS[raw];
      if (!bg) return <span className="text-gray-400">—</span>;
      return (
        <span
          className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white"
          style={{ background: bg }}
        >
          {raw}
        </span>
      );
    }

    const text = format_field_value(field.shape, raw);
    if (field.shape === 'yes_no' || field.shape === 'yes_no_na') {
      const color = raw === 'Yes' ? '#16a34a' : raw === 'No' ? '#dc2626' : undefined;
      return <span style={{ color, fontWeight: color ? 600 : 400 }}>{text}</span>;
    }
    return <span className={text === '—' ? 'text-gray-400' : ''}>{text}</span>;
  };

  render() {
    const { loading, checkpoints } = this.state;

    if (loading) return <CustomLoading />;

    if (!checkpoints.length) {
      return (
        <div className="py-10 text-center text-gray-500">
          No early-retention checklists found for this member yet.
        </div>
      );
    }

    return (
      <div className="max-h-[70vh] overflow-auto">
        <table className="text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 text-left px-4 py-2 font-semibold text-gray-700 border-b border-r border-gray-200 min-w-[280px]">
                Field
              </th>
              {checkpoints.map((cp) => (
                <th
                  key={cp.check_type}
                  className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap min-w-[140px]"
                >
                  <div>{this.col_label(cp)}</div>
                  {cp.checkin_date ? (
                    <div className="text-xs font-normal text-gray-400">{cp.checkin_date}</div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-white">
              <td
                className="sticky left-0 z-10 px-4 py-2 text-gray-700 border-r border-gray-200 bg-inherit align-top"
                style={{ textAlign: 'left' }}
              >
                Checklist filled by
              </td>
              {checkpoints.map((cp) => (
                <td
                  key={cp.check_type}
                  className="px-4 py-2 text-gray-800 align-top whitespace-nowrap"
                  style={{ textAlign: 'left' }}
                >
                  {cp.agent_name || <span className="text-gray-400">—</span>}
                </td>
              ))}
            </tr>
            {this.build_rows().map((row, idx) => (
              <tr key={row.key} className={idx % 2 ? 'bg-white' : 'bg-gray-50/40'}>
                <td
                  className="sticky left-0 z-10 px-4 py-2 text-gray-700 border-r border-gray-200 bg-inherit align-top"
                  style={{ textAlign: 'left' }}
                >
                  {row.parts.map((f) => (
                    <div key={f.key} className="py-0.5">
                      {f.label}
                    </div>
                  ))}
                </td>
                {checkpoints.map((cp) => (
                  <td
                    key={cp.check_type}
                    className="px-4 py-2 text-gray-800 align-top whitespace-nowrap"
                    style={{ textAlign: 'left' }}
                  >
                    {row.parts.map((f) => (
                      <div key={f.key} className="py-0.5">
                        {this.render_cell(f, cp)}
                      </div>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

HealthHistory.propTypes = {
  student_uuid: PropTypes.string.isRequired
};

export default HealthHistory;
